/**
 * Duration formatting utilities for the Daily Improvement module
 */

/**
 * Format minutes to human-readable duration string
 * @param {number} minutes - Total minutes
 * @returns {string} Formatted duration (e.g., "1h 30m", "45m", "2h")
 */
export function formatDuration(minutes) {
  if (!minutes || minutes <= 0) return '0m'
  
  const hours = Math.floor(minutes / 60)
  const mins = minutes % 60
  
  if (hours === 0) {
    return `${mins}m`
  }
  
  if (mins === 0) {
    return `${hours}h`
  }
  
  return `${hours}h ${mins}m`
}

/**
 * Format minutes to compact duration string for tight spaces
 * @param {number} minutes - Total minutes
 * @returns {string} Compact formatted duration (e.g., "1:30", "0:45", "2:00")
 */
export function formatDurationCompact(minutes) {
  if (!minutes || minutes <= 0) return '0:00'
  
  const hours = Math.floor(minutes / 60)
  const mins = minutes % 60
  
  return `${hours}:${mins.toString().padStart(2, '0')}`
}

/**
 * Parse duration string to minutes
 * Accepts formats: "1h 30m", "1h30m", "90m", "1.5h", "1h", "30"
 * @param {string} input - Duration string
 * @returns {number} Total minutes
 */
export function parseDuration(input) {
  if (!input) return 0
  
  const str = input.toString().trim().toLowerCase()
  
  // If just a number, assume minutes
  if (/^\d+$/.test(str)) {
    return parseInt(str, 10)
  }
  
  // Match patterns like "1h30m", "1h 30m", "1h", "30m"
  const hoursMatch = str.match(/(\d+(?:\.\d+)?)\s*h/)
  const minsMatch = str.match(/(\d+)\s*m/)
  
  let totalMinutes = 0
  
  if (hoursMatch) {
    totalMinutes += parseFloat(hoursMatch[1]) * 60
  }
  
  if (minsMatch) {
    totalMinutes += parseInt(minsMatch[1], 10)
  }
  
  return Math.round(totalMinutes)
}

/**
 * Convert hours and minutes to total minutes
 * @param {number} hours - Hours
 * @param {number} minutes - Minutes
 * @returns {number} Total minutes
 */
export function toMinutes(hours, minutes) {
  return (hours || 0) * 60 + (minutes || 0)
}

/**
 * Convert total minutes to hours and minutes object
 * @param {number} totalMinutes - Total minutes
 * @returns {{ hours: number, minutes: number }}
 */
export function fromMinutes(totalMinutes) {
  const hours = Math.floor(totalMinutes / 60)
  const minutes = totalMinutes % 60
  return { hours, minutes }
}

/**
 * Get duration presets for quick selection
 * @returns {Array<{ label: string, minutes: number }>}
 */
export function getDurationPresets() {
  return [
    { label: '15m', minutes: 15 },
    { label: '30m', minutes: 30 },
    { label: '45m', minutes: 45 },
    { label: '1h', minutes: 60 },
    { label: '1h 30m', minutes: 90 },
    { label: '2h', minutes: 120 },
  ]
}
